import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { colors } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import moment, { Moment } from 'moment';
import React, { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import Spacings from '~/modules/ui-kit/spacings';
import { CalendarEventDto } from '~/generated';

const isEventOnSameDay = (calendarEvent: CalendarEvent, date: Moment) =>
  moment(calendarEvent.start).startOf('day').isSame(date, 'day') ||
  moment(calendarEvent.end).startOf('day').isSame(date, 'day');

const cellBaseStyles = (theme: Theme) => ({
  borderTop: 'none' as const,
  borderRight: 'none' as const,
  borderLeft: `1px solid ${theme.palette.grey[300]}`,
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  padding: `${theme.spacing(0)} ${theme.spacing(0.5)}`,
  textAlign: 'center' as const,
  appearance: 'none' as const,
  backgroundColor: 'transparent',
});

const CalendarGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'auto repeat(var(--numberOfDays, 7), minmax(0, 1fr))',
  gridTemplateRows: 'auto repeat(48, minmax(0, 1fr))',
  userSelect: 'none',
});

const CalendarCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'oddRow',
})<{ highlighted?: boolean; oddRow?: boolean }>(
  ({ theme, highlighted, oddRow }) => ({
    ...cellBaseStyles(theme),
    ...(highlighted && { backgroundColor: theme.palette.grey[200] }),
    ...(oddRow && { borderBottomStyle: 'dotted' as const }),
  })
);

const CalendarCellButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'oddRow',
})<{ highlighted?: boolean; oddRow?: boolean }>(
  ({ theme, highlighted, oddRow }) => ({
    ...cellBaseStyles(theme),
    ...(highlighted && { backgroundColor: theme.palette.grey[200] }),
    ...(oddRow && { borderBottomStyle: 'dotted' as const }),
  })
);

const StyledHeaderCell = styled('div')(({ theme }) => ({
  ...cellBaseStyles(theme),
  position: 'sticky',
  top: '0px',
  backgroundColor: theme.palette.common.white,
  zIndex: 1,
}));

const EventSlot = styled('div')({
  background: 'none',
  border: 0,
  borderRadius: 0,
  margin: '1px 0',
  display: 'flex',
  alignItems: 'stretch',
  '& > *': {
    flexGrow: 1,
    boxSizing: 'border-box',
  },
});

const AllDayEvent = styled('div')(({ theme }) => ({
  background: colors.blue[50],
  color: colors.blueGrey[800],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25),
}));

const isCellInRange = (
  { day, hour }: { day: number; hour: number },
  range: CreationRange
) => {
  const isAfterFrom =
    day > range.from.day || (day === range.from.day && hour >= range.from.hour);
  const isBeforeTo =
    day < range.to.day || (day === range.to.day && hour < range.to.hour);
  return isAfterFrom && isBeforeTo;
};

type HeaderCellProps = {
  date: string;
  projectCalendarEvents?: CalendarEventDto[];
};

const HeaderCell = (props: HeaderCellProps) => {
  return (
    <StyledHeaderCell>
      <Typography variant="subtitle2">
        {moment(props.date).format('dd. Do')}
      </Typography>
      {props.projectCalendarEvents && props.projectCalendarEvents.length > 0 ? (
        <Box sx={{ py: 0.5 }}>
          <Spacings.Stack scale="xs">
            {props.projectCalendarEvents.map((externalEvent) => (
              <AllDayEvent key={externalEvent.id}>
                <Typography variant="body2">{externalEvent.name}</Typography>
              </AllDayEvent>
            ))}
          </Spacings.Stack>
        </Box>
      ) : null}
    </StyledHeaderCell>
  );
};

type CalendarEvent = {
  id: number;
  start: string;
  end: string;
};

type CalendarProps<T> = {
  onCreateShift?: (options: { startDate: string; endDate: string }) => void;
  onEventRender: (event: T) => React.ReactNode;
  events: T[];
  date: Moment;
  displayType: 'day' | 'week';
  projectCalendarEvents?: HeaderCellProps['projectCalendarEvents'];
};

type CreationRange = {
  from: {
    day: number;
    hour: number;
  };
  to: {
    day: number;
    hour: number;
  };
};

const Calendar = <T extends CalendarEvent>(props: CalendarProps<T>) => {
  const initialHourRef = useRef<HTMLSpanElement>(null);
  const [creationRange, setCreationRange] = useState<CreationRange | null>(
    null
  );
  useLayoutEffect(() => {
    if (initialHourRef.current) {
      initialHourRef.current.scrollIntoView();
    }
  }, []);
  const numberOfDays = props.displayType === 'week' ? 7 : 1;
  return (
    <CalendarGrid style={{ '--numberOfDays': numberOfDays } as CSSProperties}>
      <StyledHeaderCell />
      {props.displayType === 'week' ? (
        Array.from({ length: numberOfDays }).map((_, dayIndex) => {
          const dayDate = props.date.weekday(dayIndex).startOf('day');
          return (
            <HeaderCell
              key={dayDate.toISOString()}
              date={dayDate.toISOString()}
              projectCalendarEvents={
                props.projectCalendarEvents?.filter(
                  (externalEvent) =>
                    externalEvent.date === dayDate.format('YYYY-MM-DD')
                ) || []
              }
            />
          );
        })
      ) : (
        <HeaderCell
          date={props.date.toISOString()}
          projectCalendarEvents={
            props.projectCalendarEvents?.filter(
              (externalEvent) =>
                externalEvent.date === props.date.format('YYYY-MM-DD')
            ) || []
          }
        />
      )}
      {Array.from({ length: 48 }).map((_, hourIndex) => {
        const hour = hourIndex / (48 / 24);
        const minute = (hour % 1) * 60;
        const isHalfHour = hourIndex % 2 === 1;
        return (
          <React.Fragment key={hourIndex}>
            <CalendarCell
              oddRow={!isHalfHour}
              style={{ gridRowStart: hourIndex + 2, gridColumnStart: 1 }}
            >
              <Typography
                variant="body2"
                ref={hour === 6 ? initialHourRef : undefined}
              >
                {isHalfHour
                  ? null
                  : moment().hour(hour).minute(minute).format('LT')}
              </Typography>
            </CalendarCell>
            {Array.from({ length: numberOfDays }).map((_day, dayIndex) => {
              const isHighlighted =
                !!creationRange &&
                isCellInRange(
                  { day: dayIndex, hour: hourIndex },
                  creationRange
                );
              const cellStyle = {
                gridRowStart: hourIndex + 2,
                gridColumnStart: dayIndex + 2,
              };
              return props.onCreateShift ? (
                <CalendarCellButton
                  key={`${hourIndex}-${dayIndex}`}
                  type="button"
                  data-testid={`${dayIndex}-${hourIndex}`}
                  aria-label="Schicht anlegen"
                  oddRow={!isHalfHour}
                  highlighted={isHighlighted}
                  style={cellStyle}
                  onMouseMove={() => {
                    if (!creationRange) {
                      return;
                    }
                    setCreationRange((state) =>
                      state
                        ? {
                            from: state.from,
                            to: { day: dayIndex, hour: hourIndex + 1 },
                          }
                        : null
                    );
                  }}
                  onMouseUp={() => {
                    if (!creationRange || !props.onCreateShift) {
                      return;
                    }
                    props.onCreateShift({
                      startDate: props.date
                        .weekday(creationRange.from.day)
                        .hour(creationRange.from.hour / 2)
                        .minute(((creationRange.from.hour / 2) % 1) * 60)
                        .format('YYYY-MM-DDTHH:mm:ss'),
                      endDate: props.date
                        .weekday(creationRange.to.day)
                        .hour(creationRange.to.hour / 2)
                        .minute(((creationRange.to.hour / 2) % 1) * 60)
                        .format('YYYY-MM-DDTHH:mm:ss'),
                    });
                    setCreationRange(null);
                  }}
                  onMouseDown={() => {
                    if (!props.onCreateShift) {
                      return;
                    }
                    setCreationRange({
                      from: { day: dayIndex, hour: hourIndex },
                      to: { day: dayIndex, hour: hourIndex + 1 },
                    });
                  }}
                />
              ) : (
                <CalendarCell
                  key={`${hourIndex}-${dayIndex}`}
                  oddRow={!isHalfHour}
                  highlighted={isHighlighted}
                  style={cellStyle}
                />
              );
            })}
          </React.Fragment>
        );
      })}
      {props.events
        .filter((event) =>
          props.displayType === 'day'
            ? isEventOnSameDay(event, props.date)
            : event
        )
        .map((event) => {
          const endsOnNextDay = moment(event.end)
            .startOf('day')
            .isAfter(moment(event.start).startOf('day'));
          return (
            <React.Fragment key={event.id}>
              <EventSlot
                style={{
                  // +1 because of the day of week row,
                  // and another +1 because grid index starts at 1, but hour at 0
                  gridRowStart:
                    moment(event.start).hour() * 2 +
                    2 +
                    // and potentially another +1 if the shift starts on a half hour
                    (moment(event.start).minute() === 30 ? 1 : 0),
                  // +1 because of the day of week row,
                  // and another +1 because grid index starts at 1, but hour at 0
                  gridRowEnd: endsOnNextDay
                    ? 24 * 2 + 2
                    : moment(event.end).hour() * 2 +
                      2 +
                      // and potentially another +1 if the shift ends on a half hour
                      (moment(event.end).minute() === 30 ? 1 : 0),
                  // +1 because of the time column,
                  // and another +1 because grid index starts at 1, but moment day index at 0
                  gridColumnStart:
                    props.displayType === 'day'
                      ? 2
                      : moment(event.start).weekday() + 2,
                }}
              >
                {props.onEventRender(event)}
              </EventSlot>
              {props.displayType === 'week' && endsOnNextDay ? (
                <EventSlot
                  style={{
                    // +1 because of the day of week row,
                    // and another +1 because grid index starts at 1, but hour at 0
                    gridRowStart: 2,
                    // +1 because of the day of week row,
                    // and another +1 because grid index starts at 1, but hour at 0
                    // and potentially another +1 if the shift ends on a half hour
                    gridRowEnd:
                      moment(event.end).hour() * 2 +
                      2 +
                      (moment(event.end).minute() === 30 ? 1 : 0),
                    // +1 because of the time column,
                    // and another +1 because grid index starts at 1, but moment day index at 0
                    gridColumnStart: moment(event.end).weekday() + 2,
                  }}
                >
                  {props.onEventRender(event)}
                </EventSlot>
              ) : null}
            </React.Fragment>
          );
        })}
    </CalendarGrid>
  );
};

export default Calendar;
